'use client';

import { useState, useCallback, useRef } from 'react';
import { 
  ArrowLeft, 
  Sparkles, 
  Edit2, 
  Copy, 
  Clock,
  Save,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { RichTextEditor } from '@/components/shared/lexical-editor';
import { useToast } from '@/hooks/use-toast';
import type { DiscoveryInterview } from '@/hooks/use-discovery';

interface InterviewDetailViewProps {
  interview: DiscoveryInterview;
  projectName?: string;
  onBack: () => void;
  onSave?: (data: Partial<DiscoveryInterview>) => Promise<void>;
  onAskAI?: (message: string) => Promise<void>;
  onCopy?: () => void;
}

const influenceColors: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700 border-red-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-green-100 text-green-700 border-green-200',
};

/** Build a single HTML document from all interview fields */
function buildInterviewHtml(interview: DiscoveryInterview): string {
  const parts: string[] = [];
  parts.push(`<h1>${interview.stakeholderName || 'Unnamed Stakeholder'}</h1>`);
  if (interview.stakeholderTitle || interview.stakeholderOrg) {
    const subtitle = [interview.stakeholderTitle, interview.stakeholderOrg].filter(Boolean).join(' at ');
    parts.push(`<p>${subtitle}</p>`);
  }
  if (interview.keyInsights) {
    parts.push(`<h2>Key Insights</h2>`);
    parts.push(`<p>${interview.keyInsights.replace(/\n/g, '<br/>')}</p>`);
  }
  if (interview.painPoints) {
    parts.push(`<h2>Pain Points</h2>`);
    parts.push(`<p>${interview.painPoints.replace(/\n/g, '<br/>')}</p>`);
  }
  if (interview.fullNotes) {
    parts.push(`<h2>Full Notes</h2>`);
    parts.push(`<p>${interview.fullNotes.replace(/\n/g, '<br/>')}</p>`);
  }
  return parts.join('\n');
}

/** Parse HTML from the editor back into fields for backend save */
function parseInterviewHtml(html: string): { fields: Partial<DiscoveryInterview>; html: string } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (el: Element | null): string => el?.textContent?.trim() || '';

  const h1 = div.querySelector('h1');
  const fields: Partial<DiscoveryInterview> = { stakeholderName: text(h1) };
  const h2s = div.querySelectorAll('h2');

  h2s.forEach((h2) => {
    const sectionName = text(h2).toLowerCase();
    const contentParts: string[] = [];
    let sibling = h2.nextElementSibling;
    while (sibling && sibling.tagName !== 'H2' && sibling.tagName !== 'H1') {
      contentParts.push(sibling.textContent?.trim() || '');
      sibling = sibling.nextElementSibling;
    }
    const content = contentParts.filter(Boolean).join('\n');

    if (sectionName.includes('key insight')) fields.keyInsights = content;
    else if (sectionName.includes('pain point')) fields.painPoints = content;
    else if (sectionName.includes('full note')) fields.fullNotes = content;
  });

  return { fields, html };
}

export function InterviewDetailView({
  interview,
  projectName = 'Project',
  onBack,
  onSave,
  onAskAI,
  onCopy,
}: InterviewDetailViewProps) {
  const { toast } = useToast();
  const [showAskAI, setShowAskAI] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [savedData, setSavedData] = useState<Partial<DiscoveryInterview> | null>(null);
  const [savedHtml, setSavedHtml] = useState('');
  const initialEditorHtmlRef = useRef('');
  const handleEditorChange = useCallback((html: string) => {
    if (!initialEditorHtmlRef.current) {
      initialEditorHtmlRef.current = html;
    }
    setEditedHtml(html);
  }, []);

  const hasChanges = isEditing && editedHtml !== '' && editedHtml !== initialEditorHtmlRef.current;

  const display = savedData ? { ...interview, ...savedData } as DiscoveryInterview : interview;

  const handleSaveChanges = async () => {
    if (!onSave || !editedHtml) return;
    setIsSaving(true);
    try {
      const { fields, html } = parseInterviewHtml(editedHtml);
      await onSave(fields);
      setSavedData(fields);
      setSavedHtml(html);
      setIsEditing(false);
      setEditedHtml('');
      initialEditorHtmlRef.current = '';
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedHtml('');
    initialEditorHtmlRef.current = '';
  };

  const handleAskAI = async (message: string) => {
    if (onAskAI) {
      await onAskAI(message);
    }
    return { response: '', citations: [] };
  };

  const handleCopy = () => {
    const content = JSON.stringify(interview, null, 2);
    navigator.clipboard.writeText(content);
    onCopy?.();
    toast({
      title: 'Copied',
      description: 'Interview copied to clipboard',
    });
  };

  const metadata = (interview as any).metadata;
  const isDraft = metadata?.isDraft;
  const editorContent = savedHtml || buildInterviewHtml(display);

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER */}
      <div className="shrink-0 w-full">
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-8 py-4">
            <button 
              onClick={() => {
                if (isEditing) {
                  handleCancelEdit();
                  return;
                }
                onBack();
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isEditing ? 'Back' : 'Back to Interviews'}</span>
            </button>
            {isEditing ? (
              <button 
                onClick={handleSaveChanges}
                disabled={isSaving || !hasChanges}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  hasChanges 
                    ? "text-white bg-purple-600 hover:bg-purple-700" 
                    : "text-gray-400 bg-gray-200 cursor-not-allowed",
                  isSaving && "opacity-50"
                )}
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-start justify-between px-8 py-4 gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900 truncate">{display.stakeholderName}</h1>
              <div className="flex items-center gap-2 mt-2 text-gray-500">
                {interview.stakeholderTitle && <span>{interview.stakeholderTitle}</span>}
                {interview.stakeholderTitle && interview.stakeholderOrg && <span>•</span>}
                {interview.stakeholderOrg && <span>{interview.stakeholderOrg}</span>}
              </div>
              <div className="flex items-center gap-2 mt-2">
                {interview.influence && (
                  <span className={cn("px-2.5 py-0.5 text-xs font-medium rounded-full border", influenceColors[interview.influence])}>
                    {interview.influence.charAt(0) + interview.influence.slice(1).toLowerCase()} Influence
                  </span>
                )}
                {isDraft && (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Draft
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowAskAI(!showAskAI)}
                className="h-[42px] px-4 flex items-center gap-2 rounded-full transition-colors text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Ask AI</span>
              </button>
              <button 
                onClick={handleCopy}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
              >
                <Copy className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Copy</span>
              </button>
            </div>
          </div>
        </div>

        {showAskAI && (
          <div className="w-full bg-gray-50 border-b border-gray-200 py-4">
            <div className="px-8">
              <AskAIBar isOpen={showAskAI} onClose={() => setShowAskAI(false)} onSend={handleAskAI} placeholder="Ask AI about this interview..." />
            </div>
          </div>
        )}
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 min-h-0 overflow-y-auto w-full bg-gray-50">
        <div className="container px-8 py-6">
          <div className={cn(
            "bg-white rounded-xl mb-6 transition-all overflow-hidden",
            isEditing 
              ? "border-2 border-purple-400 shadow-[0_0_0_4px_rgba(147,51,234,0.1)]" 
              : "border border-gray-200"
          )}>
            {isEditing ? (
              <RichTextEditor
                key="editing"
                initialContent={editorContent}
                onChange={handleEditorChange}
                placeholder="Edit your interview content..."
                editable
                minHeight="150px"
              />
            ) : (
              <RichTextEditor
                key="readonly"
                initialContent={editorContent}
                editable={false}
                minHeight="150px"
              />
            )}
          </div>

          {/* Metadata Footer Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <span className="font-medium">Created by:</span> 
                {interview.createdBy?.name || interview.createdBy?.email || 'Unknown'}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {new Date(interview.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
